/**
 * "Location" is a US-vs-Puerto-Rico split that is INDEPENDENT of language
 * (`SupportedLocale`). Locale decides what language the UI renders in; location
 * decides which jurisdiction's rules, disclosures and features apply — so it
 * must hold regardless of whether the user is reading English or Spanish, in
 * order to comply with local law.
 *
 * Resolution order (weakest → strongest), see LocationProvider / LocationAddressSync:
 *   1. `default`  — US, the safe fallback when nothing else is known.
 *   2. `timezone` — synchronous device-timezone guess, shown instantly before
 *                   the IP lookup returns (America/Puerto_Rico → PR).
 *   3. `ip`       — server geo lookup of the request IP (best pre-address signal).
 *   4. `address`  — the address entered during onboarding.
 *   5. `user`     — the authenticated user's persisted `currentAddress.state`.
 *
 * Location is deliberately NOT persisted to the server: once a user is onboarded
 * their Address is the source of truth, so we always re-derive from it.
 */

export const LOCATIONS = ['US', 'PR'] as const;

export type Location = (typeof LOCATIONS)[number];

export const DEFAULT_LOCATION: Location = 'US';

// The two-letter state/subdivision abbreviation for Puerto Rico.
export const PR_STATE = 'PR';

export type LocationSource = 'default' | 'timezone' | 'ip' | 'address' | 'user' | 'override';

// Higher rank = more authoritative. A new signal only overrides the current one
// when its rank is >= the current source's rank (see LocationProvider).
// `override` sits above everything so a `?location=` query param wins over
// IP/address/user. It is honored in ALL environments, including production.
export const LOCATION_SOURCE_RANK: Record<LocationSource, number> = {
  default: 0,
  timezone: 1,
  ip: 2,
  address: 3,
  user: 4,
  override: 5,
};

export const isLocation = (value: string | null | undefined): value is Location =>
  !!value && (LOCATIONS as readonly string[]).includes(value);

/**
 * Derive location from a US state / ISO 3166-2 subdivision abbreviation — the
 * signal we get from an entered address (onboarding or `currentAddress.state`).
 * Any state other than PR is treated as US. Returns undefined when there is no
 * state to read, so callers can fall back to a weaker signal.
 */
export const locationFromState = (state: string | null | undefined): Location | undefined => {
  if (!state) return undefined;
  return state.trim().toUpperCase() === PR_STATE ? 'PR' : 'US';
};

interface GeoInput {
  country?: string | null; // ISO 3166-1 alpha-2 (PR is its own country code)
  region?: string | null; // ISO 3166-2 subdivision, e.g. 'PR' or 'CA' when country=US
}

/**
 * Derive location from an IP geo lookup. Puerto Rico is its own ISO country
 * code (`PR`) in most databases (Cloudflare, MaxMind), but some place it as a
 * US subdivision — accept either. Anything outside US/PR returns undefined so
 * the caller keeps the current (default) location rather than guessing.
 */
export const locationFromGeo = ({ country, region }: GeoInput): Location | undefined => {
  const c = country?.trim().toUpperCase();
  const r = region?.trim().toUpperCase();
  if (c === PR_STATE || r === PR_STATE) return 'PR';
  if (c === 'US') return 'US';
  return undefined;
};

/**
 * Read a location override from a URL query string, e.g. `?location=PR`. Honored
 * in all environments (production included) as the highest-ranked source, so a
 * `?location=` param wins over IP/address/user. Accepts `PR` or `US`
 * (case-insensitive); returns undefined for anything else.
 */
export const locationFromQueryString = (search: string | null | undefined): Location | undefined => {
  if (!search) return undefined;
  const value = new URLSearchParams(search).get('location')?.trim().toUpperCase();
  return isLocation(value) ? value : undefined;
};

/**
 * Synchronous best-effort guess from the device timezone, used to render a
 * reasonable location instantly before the async IP lookup resolves. Only
 * returns PR (a positive signal); everything else falls through to the default.
 */
export const locationFromTimezone = (timezone: string | undefined): Location | undefined =>
  timezone === 'America/Puerto_Rico' ? 'PR' : undefined;

/** The runtime device timezone, or undefined when Intl is unavailable (SSR/tests). */
export const currentTimezone = (): string | undefined =>
  typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
